import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import useMortgageApi from '../../hooks/useMortgageApi';

const MortgageCheck = () => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    clearError,
    checkEligibility,
    calculatePayment,
    checkAffordability,
    helpers,
  } = useMortgageApi();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    income: '',
    expenses: '',
    deposit: '',
    propertyPrice: '',
    loanTerm: '30',
    creditScore: '700',
    interestRate: '6.0',
    repaymentType: 'principal_and_interest',
  });
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('eligibility');

  const mockUser = {
    id: 1,
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@email.com',
    role: 'user',
  };

  const handleLogout = () => navigate('/authentication');

  const handleCalculate = async () => {
    clearError();
    const income = parseFloat(formData?.income) || 0;
    const expenses = parseFloat(formData?.expenses) || 0;
    const deposit = parseFloat(formData?.deposit) || 0;
    const propertyPrice = parseFloat(formData?.propertyPrice) || 0;
    const loanTerm = parseInt(formData?.loanTerm) || 30;
    const creditScore = parseInt(formData?.creditScore) || 700;
    const interestRate = parseFloat(formData?.interestRate) || 6.0;
    const loanAmount = propertyPrice - deposit;

    try {
      const [eligibility, payment, affordability] = await Promise.all([
        checkEligibility({
          annualIncome: income,
          monthlyExpenses: expenses,
          deposit,
          propertyPrice,
          loanTerm,
          creditScore,
        }),
        calculatePayment({
          loanAmount,
          interestRate,
          loanTerm,
          repaymentType: formData?.repaymentType,
        }),
        checkAffordability({
          annualIncome: income,
          monthlyExpenses: expenses,
          deposit,
        }),
      ]);

      setResults({ eligibility, payment, affordability });
      setStep(2);
      setActiveTab('eligibility');
    } catch (err) {
      // error is already set in hook state
    }
  };

  const fmt = (val) => helpers?.formatCurrency(val) ?? `$${(val || 0)?.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        activeTab === id
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    >
      <Icon name={icon} size={15} />
      {label}
    </button>
  );

  return (
    <>
      <Helmet>
        <title>Mortgage Check - ListingLens</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={mockUser} onLogout={handleLogout} />

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">10-Minute Mortgage Check</h1>
            <p className="text-muted-foreground">Get instant insights into your borrowing power</p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={18} color="var(--color-destructive)" className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">{error?.message}</p>
                {error?.code && <p className="text-xs text-muted-foreground mt-1">Error code: {error?.code}</p>}
              </div>
              <button onClick={clearError} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div className="bg-card rounded-xl border border-border p-6 md:p-8">
            {step === 1 ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Annual Income"
                    type="number"
                    placeholder="80000"
                    value={formData?.income}
                    onChange={(e) => setFormData({ ...formData, income: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Monthly Expenses"
                    type="number"
                    placeholder="3000"
                    value={formData?.expenses}
                    onChange={(e) => setFormData({ ...formData, expenses: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Deposit Amount"
                    type="number"
                    placeholder="100000"
                    value={formData?.deposit}
                    onChange={(e) => setFormData({ ...formData, deposit: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Target Property Price"
                    type="number"
                    placeholder="500000"
                    value={formData?.propertyPrice}
                    onChange={(e) => setFormData({ ...formData, propertyPrice: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Interest Rate (%)"
                    type="number"
                    placeholder="6.0"
                    value={formData?.interestRate}
                    onChange={(e) => setFormData({ ...formData, interestRate: e?.target?.value })}
                  />
                  <Input
                    label="Credit Score"
                    type="number"
                    placeholder="700"
                    value={formData?.creditScore}
                    onChange={(e) => setFormData({ ...formData, creditScore: e?.target?.value })}
                  />
                  <Select
                    label="Loan Term"
                    value={formData?.loanTerm}
                    onChange={(e) => setFormData({ ...formData, loanTerm: e?.target?.value })}
                  >
                    <option value="15">15 years</option>
                    <option value="20">20 years</option>
                    <option value="25">25 years</option>
                    <option value="30">30 years</option>
                  </Select>
                  <Select
                    label="Repayment Type"
                    value={formData?.repaymentType}
                    onChange={(e) => setFormData({ ...formData, repaymentType: e?.target?.value })}
                  >
                    <option value="principal_and_interest">Principal & Interest</option>
                    <option value="interest_only">Interest Only</option>
                  </Select>
                </div>

                <Button
                  variant="default"
                  fullWidth
                  onClick={handleCalculate}
                  disabled={loading || !formData?.income || !formData?.propertyPrice}
                  iconName={loading ? undefined : 'Calculator'}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Analysing your mortgage...
                    </span>
                  ) : (
                    'Calculate Borrowing Power'
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Status Header */}
                <div className="text-center mb-2">
                  <div
                    className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      results?.eligibility?.eligible ? 'bg-success/10' : 'bg-warning/10'
                    }`}
                  >
                    <Icon
                      name={results?.eligibility?.eligible ? 'CheckCircle2' : 'AlertCircle'}
                      size={40}
                      color={results?.eligibility?.eligible ? 'var(--color-success)' : 'var(--color-warning)'}
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-1">
                    {results?.eligibility?.eligible ? 'Eligible for a Mortgage!' : 'Review Required'}
                  </h2>
                  <p className="text-muted-foreground text-sm">{results?.eligibility?.summary}</p>
                  {results?.eligibility?.eligibilityScore != null && (
                    <div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 bg-muted rounded-full">
                      <Icon name="Star" size={14} color="var(--color-primary)" />
                      <span className="text-sm font-semibold text-foreground">
                        Eligibility Score: {results?.eligibility?.eligibilityScore}/100
                      </span>
                    </div>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 border-b border-border pb-3">
                  <TabButton id="eligibility" label="Eligibility" icon="ShieldCheck" />
                  <TabButton id="payments" label="Payments" icon="CreditCard" />
                  <TabButton id="affordability" label="Affordability" icon="TrendingUp" />
                  {results?.payment?.amortizationSchedule?.length > 0 && (
                    <TabButton id="schedule" label="Schedule" icon="Table" />
                  )}
                </div>

                {/* Eligibility Tab */}
                {activeTab === 'eligibility' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Debt-to-Income</p>
                        <p className="text-lg font-bold text-foreground">
                          {((results?.eligibility?.details?.debtToIncomeRatio || 0) * 100)?.toFixed(1)}%
                        </p>
                        <p className="text-xs text-muted-foreground">Limit: 43%</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Loan-to-Value</p>
                        <p className="text-lg font-bold text-foreground">
                          {results?.eligibility?.details?.loanToValueRatio?.toFixed(1)}%
                        </p>
                        <p className="text-xs text-muted-foreground">LVR</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Deposit</p>
                        <p className="text-lg font-bold text-foreground">
                          {results?.eligibility?.details?.depositPercentage?.toFixed(1)}%
                        </p>
                        <p className="text-xs text-muted-foreground">of property</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Credit Score</p>
                        <p className="text-lg font-bold text-foreground">
                          {results?.eligibility?.details?.creditScoreStatus}
                        </p>
                        <p className="text-xs text-muted-foreground">Rating</p>
                      </div>
                    </div>

                    {/* Flags */}
                    {results?.eligibility?.flags?.length > 0 && (
                      <div className="space-y-2">
                        {results?.eligibility?.flags?.map((flag, i) => (
                          <div
                            key={i}
                            className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                              flag?.type === 'error' ?'bg-destructive/10 text-destructive'
                                : flag?.type === 'warning' ?'bg-warning/10 text-warning' :'bg-primary/10 text-primary'
                            }`}
                          >
                            <Icon
                              name={flag?.type === 'error' ? 'XCircle' : flag?.type === 'warning' ? 'AlertTriangle' : 'Info'}
                              size={15}
                              className="mt-0.5 shrink-0"
                            />
                            {flag?.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Next Steps */}
                    {results?.eligibility?.nextSteps?.length > 0 && (
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Icon name="ListChecks" size={15} />
                          Next Steps
                        </p>
                        <ul className="space-y-2">
                          {results?.eligibility?.nextSteps?.map((step, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Icon name="ChevronRight" size={14} color="var(--color-primary)" />
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Payments Tab */}
                {activeTab === 'payments' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-5">
                        <p className="text-xs text-muted-foreground mb-1">Monthly Payment</p>
                        <p className="text-2xl font-bold text-foreground">{fmt(results?.payment?.monthlyPayment)}</p>
                        <p className="text-xs text-muted-foreground mt-1">per month</p>
                      </div>
                      <div className="bg-muted rounded-lg p-5">
                        <p className="text-xs text-muted-foreground mb-1">Fortnightly</p>
                        <p className="text-xl font-bold text-foreground">{fmt(results?.payment?.fortnightlyPayment)}</p>
                        <p className="text-xs text-muted-foreground mt-1">per fortnight</p>
                      </div>
                      <div className="bg-muted rounded-lg p-5">
                        <p className="text-xs text-muted-foreground mb-1">Weekly</p>
                        <p className="text-xl font-bold text-foreground">{fmt(results?.payment?.weeklyPayment)}</p>
                        <p className="text-xs text-muted-foreground mt-1">per week</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Loan Amount</p>
                        <p className="text-lg font-bold text-foreground">{fmt(results?.payment?.loanAmount)}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Total Interest</p>
                        <p className="text-lg font-bold text-destructive">{fmt(results?.payment?.totalInterest)}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Total Repayment</p>
                        <p className="text-lg font-bold text-foreground">{fmt(results?.payment?.totalRepayment)}</p>
                      </div>
                    </div>

                    <div className="bg-muted rounded-lg p-4 flex flex-wrap gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Rate: <strong className="text-foreground">{results?.payment?.interestRate}% p.a.</strong>
                      </span>
                      <span className="text-muted-foreground">
                        Term: <strong className="text-foreground">{results?.payment?.loanTerm} years</strong>
                      </span>
                      <span className="text-muted-foreground">
                        Type: <strong className="text-foreground">
                          {results?.payment?.repaymentType === 'interest_only' ? 'Interest Only' : 'Principal & Interest'}
                        </strong>
                      </span>
                    </div>
                  </div>
                )}

                {/* Affordability Tab */}
                {activeTab === 'affordability' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-success/10 border border-success/20 rounded-lg p-5">
                        <p className="text-xs text-muted-foreground mb-1">Max Borrowing Power</p>
                        <p className="text-2xl font-bold text-foreground">{fmt(results?.affordability?.maxBorrowingPower)}</p>
                        <p className="text-xs text-muted-foreground mt-1">maximum loan</p>
                      </div>
                      <div className="bg-muted rounded-lg p-5">
                        <p className="text-xs text-muted-foreground mb-1">Max Property Price</p>
                        <p className="text-2xl font-bold text-foreground">{fmt(results?.affordability?.maxPropertyPrice)}</p>
                        <p className="text-xs text-muted-foreground mt-1">including deposit</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Max Monthly Repayment</p>
                        <p className="text-lg font-bold text-foreground">{fmt(results?.affordability?.maxMonthlyRepayment)}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Your Deposit</p>
                        <p className="text-lg font-bold text-foreground">{fmt(results?.affordability?.deposit)}</p>
                      </div>
                    </div>

                    {results?.affordability?.assumptions && (
                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                        <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Icon name="Info" size={14} />
                          Assumptions
                        </p>
                        <p className="text-xs text-muted-foreground">{results?.affordability?.assumptions?.note}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                          <span>Rate: {results?.affordability?.assumptions?.interestRate}% p.a.</span>
                          <span>Term: {results?.affordability?.assumptions?.loanTerm} years</span>
                          <span>Repayment ratio: {(results?.affordability?.assumptions?.repaymentRatio * 100)?.toFixed(0)}%</span>
                        </div>
                      </div>
                    )}

                    {/* Affordability vs Target */}
                    {formData?.propertyPrice && (
                      <div
                        className={`rounded-lg p-4 flex items-start gap-3 ${
                          parseFloat(formData?.propertyPrice) <= (results?.affordability?.maxPropertyPrice || 0)
                            ? 'bg-success/10 border border-success/20' :'bg-warning/10 border border-warning/20'
                        }`}
                      >
                        <Icon
                          name={
                            parseFloat(formData?.propertyPrice) <= (results?.affordability?.maxPropertyPrice || 0)
                              ? 'CheckCircle2' :'AlertTriangle'
                          }
                          size={18}
                          color={
                            parseFloat(formData?.propertyPrice) <= (results?.affordability?.maxPropertyPrice || 0)
                              ? 'var(--color-success)'
                              : 'var(--color-warning)'
                          }
                          className="mt-0.5 shrink-0"
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {parseFloat(formData?.propertyPrice) <= (results?.affordability?.maxPropertyPrice || 0)
                              ? 'Your target property is within your budget' :'Your target property exceeds your budget'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Target: {fmt(parseFloat(formData?.propertyPrice))} · Max: {fmt(results?.affordability?.maxPropertyPrice)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Amortization Schedule Tab */}
                {activeTab === 'schedule' && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-muted-foreground font-medium">Month</th>
                          <th className="text-right py-2 px-3 text-muted-foreground font-medium">Payment</th>
                          <th className="text-right py-2 px-3 text-muted-foreground font-medium">Principal</th>
                          <th className="text-right py-2 px-3 text-muted-foreground font-medium">Interest</th>
                          <th className="text-right py-2 px-3 text-muted-foreground font-medium">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results?.payment?.amortizationSchedule?.map((row) => (
                          <tr key={row?.month} className="border-b border-border/50 hover:bg-muted/50">
                            <td className="py-2 px-3 text-foreground">{row?.month}</td>
                            <td className="py-2 px-3 text-right text-foreground">{fmt(row?.payment)}</td>
                            <td className="py-2 px-3 text-right text-success">{fmt(row?.principal)}</td>
                            <td className="py-2 px-3 text-right text-destructive">{fmt(row?.interest)}</td>
                            <td className="py-2 px-3 text-right text-foreground">{fmt(row?.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-muted-foreground mt-2 px-3">Showing first 12 months of amortization schedule</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button variant="outline" fullWidth onClick={() => { setStep(1); setResults(null); clearError(); }}>
                    Recalculate
                  </Button>
                  <Button variant="default" fullWidth onClick={() => navigate('/search?mode=buy')}>
                    Browse Properties
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default MortgageCheck;